import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Copy,
  Loader2,
  RefreshCw,
  Download,
  Video,
  Wand2,
  Zap,
  BookOpen,
  Target,
  Lightbulb,
  Image,
  ChevronRight,
  History,
  Clock,
  Volume2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AIStudioTab = ({ token }) => {
  // AI tool state
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiTool, setAiTool] = useState('yt-script');

  // Tool-specific inputs
  const [tutorialTopic, setTutorialTopic] = useState('basics');
  const [tutorialDifficulty, setTutorialDifficulty] = useState('beginner');
  const [enhanceStyle, setEnhanceStyle] = useState('energetic');
  const [scriptToEnhance, setScriptToEnhance] = useState('');
  const [hookTopic, setHookTopic] = useState('');
  const [thumbnailTopic, setThumbnailTopic] = useState('');

  // YouTube Script Generator
  const [ytScriptType, setYtScriptType] = useState('market_recap');
  const [ytTone, setYtTone] = useState('educational');
  const [ytFocusAsset, setYtFocusAsset] = useState('');

  // Script History
  const [scriptHistory, setScriptHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryScript, setSelectedHistoryScript] = useState(null);

  // TTS / Voiceover
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // ===== API Functions =====
  const checkAiStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiAvailable(data.available);
      }
    } catch {
      setAiAvailable(false);
    }
  };

  const fetchScriptHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/script-history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScriptHistory(data.scripts || []);
      }
    } catch (error) {
      console.error("Error fetching script history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchVoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVoices(data.voices || []);
        if (data.voices?.length > 0 && !selectedVoice) {
          setSelectedVoice(data.voices[0].voice_id);
        }
      }
    } catch (error) {
      console.error("Error fetching voices:", error);
    }
  };

  useEffect(() => {
    if (!aiAvailable) checkAiStatus();
    if (scriptHistory.length === 0 && !historyLoading) fetchScriptHistory();
    if (voices.length === 0) fetchVoices();
  }, []);

  // ===== Generator Functions =====
  const generateTutorial = async () => {
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/tutorial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: tutorialTopic, difficulty: tutorialDifficulty })
      });
      if (res.ok) { setAiResult({ type: 'tutorial', data: await res.json() }); toast.success("Tutorial script generated!"); }
      else { const error = await res.json(); toast.error(error.detail || "Failed to generate tutorial"); }
    } catch { toast.error("Failed to generate tutorial"); }
    finally { setAiLoading(false); }
  };

  const generateWeeklyPicks = async () => {
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/weekly-picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      if (res.ok) { setAiResult({ type: 'picks', data: await res.json() }); toast.success("Weekly picks generated!"); }
      else { const error = await res.json(); toast.error(error.detail || "Failed to generate picks"); }
    } catch { toast.error("Failed to generate weekly picks"); }
    finally { setAiLoading(false); }
  };

  const enhanceScript = async () => {
    if (!scriptToEnhance.trim()) { toast.error("Please enter a script to enhance"); return; }
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/enhance-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ script: scriptToEnhance, style: enhanceStyle })
      });
      if (res.ok) { setAiResult({ type: 'enhance', data: await res.json() }); toast.success("Script enhanced!"); }
      else { const error = await res.json(); toast.error(error.detail || "Failed to enhance script"); }
    } catch { toast.error("Failed to enhance script"); }
    finally { setAiLoading(false); }
  };

  const generateHooks = async () => {
    if (!hookTopic.trim()) { toast.error("Please enter a topic for hooks"); return; }
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/hooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: hookTopic, count: 5 })
      });
      if (res.ok) { setAiResult({ type: 'hooks', data: await res.json() }); toast.success("Hook variations generated!"); }
      else { const error = await res.json(); toast.error(error.detail || "Failed to generate hooks"); }
    } catch { toast.error("Failed to generate hooks"); }
    finally { setAiLoading(false); }
  };

  const generateThumbnails = async () => {
    if (!thumbnailTopic.trim()) { toast.error("Please enter a topic for thumbnails"); return; }
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/thumbnail-ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: thumbnailTopic, count: 3 })
      });
      if (res.ok) { setAiResult({ type: 'thumbnails', data: await res.json() }); toast.success("Thumbnail ideas generated!"); }
      else { const error = await res.json(); toast.error(error.detail || "Failed to generate thumbnails"); }
    } catch { toast.error("Failed to generate thumbnail ideas"); }
    finally { setAiLoading(false); }
  };

  const generateYouTubeScript = async () => {
    if (ytScriptType === 'asset_deep_dive' && !ytFocusAsset.trim()) {
      toast.error("Please enter an asset symbol for the deep dive"); return;
    }
    setAiLoading(true); setAiResult(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/generate-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          script_type: ytScriptType, tone: ytTone,
          focus_asset: ytScriptType === 'asset_deep_dive' ? ytFocusAsset : null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult({ type: 'yt-script', data });
        toast.success("YouTube script generated!");
        fetchScriptHistory();
      } else { const error = await res.json(); toast.error(error.detail || "Failed to generate script"); }
    } catch { toast.error("Failed to generate YouTube script"); }
    finally { setAiLoading(false); }
  };

  const generateVoiceover = async (scriptText) => {
    if (!scriptText || !selectedVoice) { toast.error("Select a voice first"); return; }
    setTtsLoading(true); setAudioUrl(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/generate-voiceover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: scriptText, voice_id: selectedVoice, clean_markers: true })
      });
      if (res.ok) {
        const data = await res.json();
        setAudioUrl(`data:audio/mpeg;base64,${data.audio_base64}`);
        toast.success(`Voiceover generated! ${data.char_count} chars, ~${data.est_duration_sec}s`);
      } else { const error = await res.json(); toast.error(error.detail || "Failed to generate voiceover"); }
    } catch { toast.error("Failed to generate voiceover"); }
    finally { setTtsLoading(false); }
  };

  // ===== Helper to get script text from result =====
  const getResultText = () => {
    if (!aiResult) return '';
    switch (aiResult.type) {
      case 'yt-script': case 'tutorial': case 'picks': return aiResult.data.script;
      case 'enhance': return aiResult.data.enhanced_script;
      case 'hooks': return aiResult.data.hooks;
      case 'thumbnails': return aiResult.data.ideas;
      default: return '';
    }
  };

  const getResultDescription = () => {
    if (!aiResult) return '';
    switch (aiResult.type) {
      case 'yt-script': return `YouTube Script: ${aiResult.data.script_type?.replace(/_/g, ' ')} (${aiResult.data.tone} tone)`;
      case 'tutorial': return `Tutorial: ${aiResult.data.topic} (${aiResult.data.difficulty})`;
      case 'picks': return 'Weekly Strategy Picks';
      case 'enhance': return `Enhanced Script (${aiResult.data.style} style)`;
      case 'hooks': return `Hook Variations for: ${aiResult.data.topic}`;
      case 'thumbnails': return `Thumbnail Ideas for: ${aiResult.data.topic}`;
      default: return '';
    }
  };

  const AI_TOOLS = [
    { id: 'yt-script', name: 'YouTube Script', icon: Video, desc: 'Full video scripts with market data' },
    { id: 'tutorial', name: 'Tutorial Creator', icon: BookOpen, desc: 'How-to-play guides' },
    { id: 'picks', name: 'Weekly Picks', icon: Target, desc: 'Strategy recommendations' },
    { id: 'enhance', name: 'Script Enhancer', icon: Zap, desc: 'Polish your scripts' },
    { id: 'hooks', name: 'Hook Generator', icon: Lightbulb, desc: 'Attention-grabbing intros' },
    { id: 'thumbnails', name: 'Thumbnail Ideas', icon: Image, desc: 'Visual concepts' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tool Selection Sidebar */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-400" /> AI Tools
            </CardTitle>
            <CardDescription>Select a tool to generate content</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {AI_TOOLS.map(tool => (
              <button
                key={tool.id}
                onClick={() => { setAiTool(tool.id); setAiResult(null); }}
                className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                  aiTool === tool.id
                    ? 'bg-purple-500/20 border-purple-500 text-white'
                    : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
                data-testid={`ai-tool-${tool.id}`}
              >
                <tool.icon className={`w-5 h-5 ${aiTool === tool.id ? 'text-purple-400' : 'text-slate-500'}`} />
                <div>
                  <div className="font-medium text-sm">{tool.name}</div>
                  <div className="text-xs text-slate-500">{tool.desc}</div>
                </div>
                <ChevronRight className={`w-4 h-4 ml-auto ${aiTool === tool.id ? 'text-purple-400' : 'text-slate-600'}`} />
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Tool Configuration & Output */}
        <div className="lg:col-span-2 space-y-6">
          {/* YouTube Script Generator */}
          {aiTool === 'yt-script' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-400" /> YouTube Script Generator
                </CardTitle>
                <CardDescription>Generate data-driven video scripts with live market analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Script Type</Label>
                    <select value={ytScriptType} onChange={(e) => setYtScriptType(e.target.value)}
                      className="w-full mt-1 p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" data-testid="yt-script-type-select">
                      <option value="market_recap">Market Recap (Weekly review)</option>
                      <option value="asset_deep_dive">Asset Deep Dive (Single instrument)</option>
                      <option value="weekly_predictions">Weekly Predictions (Strategy picks)</option>
                      <option value="power_play_strategy">Power Play Strategy (3x guide)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Tone</Label>
                    <select value={ytTone} onChange={(e) => setYtTone(e.target.value)}
                      className="w-full mt-1 p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" data-testid="yt-tone-select">
                      <option value="educational">Educational (Calm & informative)</option>
                      <option value="hype">Hype (High-energy & exciting)</option>
                      <option value="professional">Professional (News anchor style)</option>
                    </select>
                  </div>
                </div>
                {ytScriptType === 'asset_deep_dive' && (
                  <div>
                    <Label className="text-slate-300">Focus Asset Symbol</Label>
                    <Input value={ytFocusAsset} onChange={(e) => setYtFocusAsset(e.target.value.toUpperCase())}
                      placeholder="e.g., AAPL, BTC, TSLA" className="bg-slate-900 border-slate-700 mt-1" data-testid="yt-focus-asset-input" />
                    <p className="text-xs text-slate-500 mt-1">Enter the ticker symbol of the instrument to analyze</p>
                  </div>
                )}
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-slate-300 text-sm font-medium mb-2">What you'll get:</p>
                  <ul className="space-y-1 text-slate-400 text-sm">
                    {['Complete script with [TIMESTAMP] markers', '[VISUAL CUE] suggestions for B-roll/graphics',
                      'Live market data baked into the script', 'Game strategy predictions & recommendations',
                      'Auto-appended legal disclaimer'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2"><ChevronRight className="w-4 h-4 text-purple-400 shrink-0" /> {item}</li>
                    ))}
                  </ul>
                </div>
                <Button onClick={generateYouTubeScript}
                  disabled={aiLoading || (ytScriptType === 'asset_deep_dive' && !ytFocusAsset.trim())}
                  className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-yt-script-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Video className="w-4 h-4 mr-2" />}
                  Generate YouTube Script
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Tutorial Creator */}
          {aiTool === 'tutorial' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-400" /> Tutorial Creator</CardTitle>
                <CardDescription>Generate complete tutorial scripts for any game topic</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Topic</Label>
                    <select value={tutorialTopic} onChange={(e) => setTutorialTopic(e.target.value)}
                      className="w-full mt-1 p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" data-testid="tutorial-topic-select">
                      <option value="basics">Getting Started (Basics)</option>
                      <option value="power_play">Power Play Guide</option>
                      <option value="scoring">Scoring System</option>
                      <option value="chips">Chips & Power-ups</option>
                      <option value="strategies">Winning Strategies</option>
                      <option value="portfolio_building">Portfolio Building</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Difficulty Level</Label>
                    <select value={tutorialDifficulty} onChange={(e) => setTutorialDifficulty(e.target.value)}
                      className="w-full mt-1 p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" data-testid="tutorial-difficulty-select">
                      <option value="beginner">Beginner (No prior knowledge)</option>
                      <option value="intermediate">Intermediate (Knows basics)</option>
                      <option value="advanced">Advanced (Expert tactics)</option>
                    </select>
                  </div>
                </div>
                <Button onClick={generateTutorial} disabled={aiLoading} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-tutorial-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Generate Tutorial Script
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Weekly Picks */}
          {aiTool === 'picks' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Target className="w-5 h-5 text-purple-400" /> Weekly Picks Generator</CardTitle>
                <CardDescription>AI-powered strategy recommendations based on current market data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <p className="text-slate-300 text-sm">This tool analyzes current instrument prices and player data to generate:</p>
                  <ul className="mt-2 space-y-1 text-slate-400 text-sm">
                    {['5 Stock picks with reasoning', '2 Crypto picks for volatility plays',
                      'Power Play recommendation', 'What to avoid this week'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2"><ChevronRight className="w-4 h-4 text-purple-400" /> {item}</li>
                    ))}
                  </ul>
                </div>
                <Button onClick={generateWeeklyPicks} disabled={aiLoading} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-picks-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Generate Weekly Picks
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Script Enhancer */}
          {aiTool === 'enhance' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Zap className="w-5 h-5 text-purple-400" /> Script Enhancer</CardTitle>
                <CardDescription>Paste your script and AI will add hooks, transitions, and energy</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Style</Label>
                  <div className="flex gap-2 mt-1">
                    {['energetic', 'professional', 'casual'].map(style => (
                      <Button key={style} variant={enhanceStyle === style ? 'default' : 'outline'} size="sm"
                        onClick={() => setEnhanceStyle(style)} className={enhanceStyle === style ? 'bg-purple-600' : ''}>
                        {style.charAt(0).toUpperCase() + style.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Your Script</Label>
                  <textarea value={scriptToEnhance} onChange={(e) => setScriptToEnhance(e.target.value)}
                    placeholder="Paste your video script here..."
                    className="w-full mt-1 p-3 bg-slate-900 border border-slate-700 rounded-lg text-white h-40 resize-none" data-testid="script-input" />
                </div>
                <Button onClick={enhanceScript} disabled={aiLoading || !scriptToEnhance.trim()} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="enhance-script-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Enhance Script
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Hook Generator */}
          {aiTool === 'hooks' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Lightbulb className="w-5 h-5 text-purple-400" /> Hook Generator</CardTitle>
                <CardDescription>Generate 5 attention-grabbing video hooks for A/B testing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Video Topic</Label>
                  <Input value={hookTopic} onChange={(e) => setHookTopic(e.target.value)}
                    placeholder="e.g., 'How I made 500 points in one week'" className="bg-slate-900 border-slate-700 mt-1" data-testid="hook-topic-input" />
                </div>
                <Button onClick={generateHooks} disabled={aiLoading || !hookTopic.trim()} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-hooks-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lightbulb className="w-4 h-4 mr-2" />}
                  Generate Hook Variations
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Thumbnail Ideas */}
          {aiTool === 'thumbnails' && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Image className="w-5 h-5 text-purple-400" /> Thumbnail Ideas</CardTitle>
                <CardDescription>Get text, visual concepts, and color schemes for thumbnails</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Video Topic</Label>
                  <Input value={thumbnailTopic} onChange={(e) => setThumbnailTopic(e.target.value)}
                    placeholder="e.g., 'Week 12 Recap - MPC Dominates'" className="bg-slate-900 border-slate-700 mt-1" data-testid="thumbnail-topic-input" />
                </div>
                <Button onClick={generateThumbnails} disabled={aiLoading || !thumbnailTopic.trim()} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="generate-thumbnails-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
                  Generate Thumbnail Ideas
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {aiLoading && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
                <p className="text-slate-300">AI is generating your content...</p>
                <p className="text-slate-500 text-sm mt-1">This may take 10-30 seconds</p>
              </CardContent>
            </Card>
          )}

          {/* AI Result Display */}
          {aiResult && !aiLoading && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Generated Content</CardTitle>
                  <CardDescription>{getResultDescription()}</CardDescription>
                </div>
                <Button variant="outline" onClick={() => copyToClipboard(getResultText(), 'Content')} data-testid="copy-ai-result-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
              </CardHeader>
              <CardContent>
                {aiResult.type === 'yt-script' && aiResult.data.word_count && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    <Badge className="bg-purple-500/20 text-purple-300">{aiResult.data.word_count} words</Badge>
                    <Badge className="bg-blue-500/20 text-blue-300">~{aiResult.data.est_duration_min} min read</Badge>
                    <Badge className="bg-emerald-500/20 text-emerald-300">{aiResult.data.tone} tone</Badge>
                    <Badge className="bg-amber-500/20 text-amber-300">{aiResult.data.script_type?.replace(/_/g, ' ')}</Badge>
                  </div>
                )}
                <div className="bg-slate-950 rounded-lg p-4 border border-slate-700 max-h-[500px] overflow-y-auto">
                  <pre className="text-slate-300 whitespace-pre-wrap text-sm font-mono">{getResultText()}</pre>
                </div>
                {aiResult.data.market_context && (
                  <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-slate-400 text-sm mb-2">Market Context Used:</p>
                    <div className="flex flex-wrap gap-2">
                      {aiResult.data.market_context.top_gainers?.map((g, i) => (
                        <Badge key={i} className="bg-emerald-500/20 text-emerald-400">{g.symbol} +{g.change_pct}%</Badge>
                      ))}
                      {aiResult.data.market_context.top_losers?.map((l, i) => (
                        <Badge key={i} className="bg-red-500/20 text-red-400">{l.symbol} {l.change_pct}%</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {/* Voiceover Generation */}
                {(aiResult.type === 'yt-script' || aiResult.type === 'picks' || aiResult.type === 'tutorial') && (
                  <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-slate-300 text-sm font-medium mb-3 flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-purple-400" /> Generate Voiceover
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)}
                        className="flex-1 p-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="voice-select">
                        {voices.length === 0 && <option value="">Loading voices...</option>}
                        {voices.map(v => (
                          <option key={v.voice_id} value={v.voice_id}>{v.name} ({v.gender}, {v.accent})</option>
                        ))}
                      </select>
                      <Button onClick={() => generateVoiceover(getResultText())}
                        disabled={ttsLoading || !selectedVoice} className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap" data-testid="generate-voiceover-btn">
                        {ttsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Volume2 className="w-4 h-4 mr-2" />}
                        {ttsLoading ? 'Generating...' : 'Generate MP3'}
                      </Button>
                    </div>
                    {audioUrl && (
                      <div className="mt-3 space-y-2">
                        <audio controls src={audioUrl} className="w-full" data-testid="audio-player" />
                        <a href={audioUrl} download="trader-titans-voiceover.mp3"
                          className="inline-flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300" data-testid="download-audio-btn">
                          <Download className="w-3 h-3" /> Download MP3
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Script History */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-purple-400" /> Script History
            </CardTitle>
            <CardDescription>Your generated scripts (auto-deleted after 14 days)</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchScriptHistory} disabled={historyLoading} data-testid="refresh-history-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant={showHistory ? 'default' : 'outline'} size="sm"
              onClick={() => setShowHistory(!showHistory)} className={showHistory ? 'bg-purple-600' : ''} data-testid="toggle-history-btn">
              {showHistory ? 'Hide' : `Show (${scriptHistory.length})`}
            </Button>
          </div>
        </CardHeader>
        {showHistory && (
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-purple-400" /></div>
            ) : scriptHistory.length === 0 ? (
              <p className="text-slate-500 text-center py-6">No scripts generated yet. Create your first one above!</p>
            ) : (
              <div className="space-y-3">
                {scriptHistory.map((script, idx) => (
                  <div key={idx}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedHistoryScript === idx ? 'bg-purple-500/10 border-purple-500/50' : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                    }`}
                    onClick={() => setSelectedHistoryScript(selectedHistoryScript === idx ? null : idx)}
                    data-testid={`history-item-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-500/20 text-purple-300 text-xs">{script.script_type?.replace(/_/g, ' ')}</Badge>
                        <Badge className="bg-blue-500/20 text-blue-300 text-xs">{script.tone}</Badge>
                        {script.focus_asset && <Badge className="bg-amber-500/20 text-amber-300 text-xs">{script.focus_asset}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-slate-500 text-xs">
                        <Clock className="w-3 h-3" />
                        {script.created_at ? new Date(script.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs">{script.word_count} words · ~{script.est_duration_min} min</p>
                    {selectedHistoryScript === idx && (
                      <div className="mt-3 space-y-2">
                        <div className="bg-slate-950 rounded-lg p-3 border border-slate-700 max-h-[300px] overflow-y-auto">
                          <pre className="text-slate-300 whitespace-pre-wrap text-xs font-mono">{script.script}</pre>
                        </div>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); copyToClipboard(script.script, 'Script'); }}
                          data-testid={`copy-history-${idx}`}>
                          <Copy className="w-3 h-3 mr-1" /> Copy Script
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};
